/**
 * Model Text Tool - 3D Text Creation
 *
 * Places 3D extruded text in the model. Click to position, then an inline
 * entry box collects the actual text (same flow as the Text Note tool).
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface ModelTextGeometryData {
    position: [number, number, number];
    text: string;
    size: number;
    depth: number;
}

export interface ModelTextElement extends Element {
    category: 'ModelText';
    geometryData: ModelTextGeometryData;
}

export class ModelTextTool extends BaseTool {
    id = 'model_text';
    name = 'Model Text';
    cursor = 'text';

    private awaitingText = false;
    private pendingPosition: THREE.Vector3 | null = null;
    private textInputHandler: ((e: Event) => void) | null = null;

    protected onActivate(): void {
        // Module-level singleton: attach the commit listener once; the handler
        // ignores events while no entry is pending.
        if (!this.textInputHandler) {
            this.textInputHandler = ((e: Event) => {
                if (!this.context || !this.awaitingText) return;
                this.awaitingText = false;

                const detail = (e as CustomEvent).detail as { text: string | null } | undefined;
                const pos = this.pendingPosition;
                this.pendingPosition = null;
                this.context.appStore.setToolPreview(null);
                if (detail?.text && pos) {
                    this.createModelText(pos, detail.text);
                }
            }) as EventListener;
            window.addEventListener('yap:text-note-input', this.textInputHandler);
        }
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        if (this.awaitingText) return; // input box already open

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.pendingPosition = snapped;
        this.awaitingText = true;
        // Swap the ghost for the in-place entry dialog (viewport renders the
        // input when toolPreview.textEntering is set).
        this.context.appStore.setToolPreview({
            type: 'model_text',
            position: snapped,
            textEntering: true
        });
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (this.awaitingText) return; // keep the entry dialog in place
        const point = this.getIntersection(event);
        if (point) {
            const snapped = this.snapToGrid(point);
            this.context.appStore.setToolPreview({
                type: 'model_text',
                position: snapped,
                text: 'Model Text'
            });
        }
    }

    onPointerUp(): void {}

    deactivate(): void {
        // Drop any open entry request so a late dialog commit cannot place a
        // stale text after the tool exits.
        this.awaitingText = false;
        this.pendingPosition = null;
        super.deactivate();
    }

    onKeyDown(event: KeyboardEvent): void {
        if (this.awaitingText && event.key === 'Escape') {
            this.awaitingText = false;
            this.pendingPosition = null;
            this.context?.appStore.setToolPreview(null);
            return;
        }
        super.onKeyDown(event);
    }

    private createModelText(point: THREE.Vector3, text: string) {
        if (!this.context) return;

        const props = this.context.bimStore.toolProperties;
        const size = (props.modelTextSize ?? 600) / 1000;   // mm → m
        const depth = (props.modelTextDepth ?? 50) / 1000;  // mm → m

        const geometryData: ModelTextGeometryData = {
            position: [point.x, point.y, point.z],
            text,
            size,
            depth
        };

        const modelText: ModelTextElement = {
            id: uuidv4(),
            category: 'ModelText',
            type: `Model Text: Arial ${(size * 1000).toFixed(0)}mm`,
            name: text,
            parameters: {
                'Text': text,
                'Depth': `${(depth * 1000).toFixed(0)} mm`,
                'Size': `${(size * 1000).toFixed(0)} mm`
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(modelText);
    }
}
